import {
  legalLinkLabel,
  type BrandConfig,
  type LegalConfig,
  type StorefrontPageProps,
} from '@kitup/brand';
import { Fragment, type CSSProperties, type JSX, type ReactNode } from 'react';
import photoBiscuit from './assets/photo-biscuit.jpg';
import photoDad from './assets/photo-dad.jpg';
import photoLily from './assets/photo-lily.jpg';
import photoMom from './assets/photo-mom.jpg';
import photoMoonbun from './assets/photo-moonbun.jpg';
import spreadPoster from './assets/spread.webp';
import { POPULAR_PLAN, planFacts, redoPack, type PlanFacts } from './facts.js';
import { Footer, Header } from './Header.js';

const PLAN_COLOUR: Readonly<Record<string, string>> = {
  book: 'var(--ta-orange)',
  live_book: 'var(--ta-sky)',
  gift_book: 'var(--ta-grass)',
};

const PLAN_SUMMARY: Readonly<Record<string, string>> = {
  book: 'A classic picture book to read on screen and print at home.',
  live_book: 'The pictures move. Every spread comes with a short animated scene.',
  gift_book: 'The keepsake: read aloud in a voice they know, ready for a quality print.',
};

/**
 * Faces for the "up to N characters" line: as many as the plan allows, the last ones blank.
 * The showcase book's cast; all generated, no real person.
 */
const FACES = [photoLily, photoMom, photoDad, photoBiscuit, photoMoonbun] as const;

function Faces({ count }: { readonly count: number }): JSX.Element {
  return (
    <span className="ta-faces" aria-hidden="true">
      {Array.from({ length: count }, (_, index) =>
        FACES[index] ? <img key={index} src={FACES[index]} alt="" /> : <span key={index} />,
      )}
    </span>
  );
}

function Fact({
  mark,
  included = true,
  children,
}: {
  readonly mark: string;
  readonly included?: boolean;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <li data-included={included ? 'true' : 'false'}>
      <i aria-hidden="true">{included ? mark : '–'}</i>
      <span>{children}</span>
    </li>
  );
}

function PlanCard({
  plan,
  Link,
  createBook,
}: {
  readonly plan: PlanFacts;
  readonly Link: StorefrontPageProps['Link'];
  readonly createBook: string;
}): JSX.Element {
  const popular = plan.id === POPULAR_PLAN;
  const headingId = `ta-plan-${plan.id}`;

  return (
    <article
      className="ta-plan"
      data-popular={popular ? 'true' : 'false'}
      aria-labelledby={headingId}
      style={{ '--c': PLAN_COLOUR[plan.id] } as CSSProperties}
    >
      {popular ? <span className="ta-flag">Most popular</span> : null}
      <div className="ta-book" style={{ '--p': plan.pages } as CSSProperties} aria-hidden="true">
        <div className="ta-book-body">
          <div className="ta-book-cover">
            <img src={spreadPoster} alt="" />
            <b />
          </div>
          <div className="ta-book-pages" />
          <div className="ta-book-shadow" />
        </div>
      </div>
      <h2 id={headingId}>{plan.name}</h2>
      <div className="ta-amount">
        {plan.price} <small>once</small>
      </div>
      <p className="ta-summary">{PLAN_SUMMARY[plan.id]}</p>
      <ul className="ta-facts">
        <Fact mark={String(plan.pages)}>
          <b>{plan.pages} pages</b>
        </Fact>
        <Fact mark={String(plan.characters)}>
          <b>Up to {plan.characters} characters</b>
          <Faces count={plan.characters} />
        </Fact>
        <Fact mark={String(plan.things)}>
          {plan.things} favorite {plan.things === 1 ? 'thing' : 'things'} and {plan.places}{' '}
          {plan.places === 1 ? 'place' : 'places'}
        </Fact>
        <Fact mark="▶" included={plan.animated}>
          {plan.animated ? (
            <>
              <b>Animated illustrations</b>
              {plan.videoQuality === '1080p' ? ', in higher quality' : null}
            </>
          ) : (
            'Still pictures'
          )}
        </Fact>
        <Fact mark="♪" included={plan.narration}>
          {plan.narration ? <b>Narration in a familiar voice</b> : 'No narration'}
        </Fact>
      </ul>
      <Link
        to={`${createBook}?plan=${plan.id}`}
        className={popular ? 'ta-btn' : 'ta-btn ta-btn-ghost'}
      >
        Choose {plan.name}
      </Link>
    </article>
  );
}

function Yes({ children }: { readonly children?: ReactNode }): JSX.Element {
  return (
    <td>
      <span className="ta-yes" aria-hidden="true">
        ✓
      </span>
      <span className="ta-sr">Included</span> {children}
    </td>
  );
}

function No(): JSX.Element {
  return (
    <td className="ta-none">
      <span aria-hidden="true">—</span>
      <span className="ta-sr">Not included</span>
    </td>
  );
}

function Comparison({ plans }: { readonly plans: readonly PlanFacts[] }): JSX.Element {
  const row = (label: ReactNode, cell: (plan: PlanFacts) => JSX.Element): JSX.Element => (
    <tr>
      <th scope="row">{label}</th>
      {plans.map((plan) => (
        <Fragment key={plan.id}>{cell(plan)}</Fragment>
      ))}
    </tr>
  );
  const group = (title: string): JSX.Element => (
    <tr data-group="">
      <th colSpan={plans.length + 1} scope="rowgroup">
        {title}
      </th>
    </tr>
  );

  return (
    <div className="ta-table-wrap">
      <table className="ta-table">
        <colgroup>
          <col />
          {plans.map((plan) => (
            <col key={plan.id} data-popular={plan.id === POPULAR_PLAN ? 'true' : 'false'} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <td />
            {plans.map((plan) => (
              <th key={plan.id} scope="col">
                {plan.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group('The story')}
          {row('Pages', (plan) => (
            <td>{plan.pages}</td>
          ))}
          {row(
            <>
              Characters<small>kids, grown-ups, pets, toys</small>
            </>,
            (plan) => (
              <td>Up to {plan.characters}</td>
            ),
          )}
          {row(
            <>
              Things and places<small>a favorite toy, the backyard, grandma’s kitchen</small>
            </>,
            (plan) => (
              <td>
                {plan.things} and {plan.places}
              </td>
            ),
          )}
          {group('The pictures')}
          {row('Illustrations', (plan) => (
            <td>{plan.highResolution ? 'High resolution (2K)' : 'Standard resolution (1K)'}</td>
          ))}
          {row('Animation', (plan) =>
            plan.videoQuality ? (
              <Yes>
                {plan.videoQuality === '1080p'
                  ? 'Enhanced quality (1080p)'
                  : 'Standard quality (720p)'}
              </Yes>
            ) : (
              <No />
            ),
          )}
          {row(
            <>
              Free redos<small>when a picture isn’t quite right</small>
            </>,
            (plan) => (
              <td>
                {plan.redos} {plan.animated ? 'illustrations or videos' : 'illustrations'}
              </td>
            ),
          )}
          {group('Reading and keeping')}
          {row('Narration in a familiar voice', (plan) => (plan.narration ? <Yes /> : <No />))}
          {row('Screen PDF and family link', () => (
            <Yes />
          ))}
          {row('Print layout', (plan) => (
            <td>
              {plan.highQualityPrint ? 'High quality, higher resolution' : 'Standard quality'}
            </td>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PricingPage({
  brand,
  legal,
  page,
}: {
  readonly brand: BrandConfig;
  readonly legal: LegalConfig;
  readonly page: StorefrontPageProps;
}): JSX.Element {
  const { Link, user } = page;
  const createBook = user ? '/app' : '/register';
  const plans = planFacts(brand);
  const pack = redoPack(brand);

  return (
    <div className="ta-storefront">
      <a href="#content" className="ta-skip">
        Skip to content
      </a>
      <Header page={page} current="pricing" />

      <main id="content" className="ta-main">
        <header className="ta-pricing-head">
          <h1 className="ta-h1">
            One book, <span className="ta-o">one price</span>
          </h1>
          <p className="ta-lede">Pick how big the adventure is. You pay once and keep the book.</p>
          <ul className="ta-pills">
            <li>First spread free</li>
            <li>No subscription</li>
            <li>Price shown before you pay</li>
          </ul>
        </header>

        <section className="ta-plans" aria-label="Plans">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} Link={Link} createBook={createBook} />
          ))}
        </section>

        <section className="ta-compare" aria-labelledby="ta-compare">
          <h2 id="ta-compare" className="ta-h2">
            Everything, <span className="ta-o">side by side</span>
          </h2>
          <p className="ta-lede">The fine print, without the fine print.</p>
          <Comparison plans={plans} />
        </section>

        <h2 id="ta-how" className="ta-h2 ta-how-head">
          How paying <span className="ta-o">works</span>
        </h2>
        <ul className="ta-how" aria-labelledby="ta-how">
          <li style={{ '--c': 'var(--ta-orange)' } as CSSProperties}>
            <b>See it first</b>
            <p>Your first spread is free. You pay only after you’ve seen the result.</p>
          </li>
          <li style={{ '--c': 'var(--ta-sky)' } as CSSProperties}>
            <b>Pay once</b>
            <p>A fixed price per book, shown before checkout. No subscription.</p>
          </li>
          <li style={{ '--c': 'var(--ta-sun)' } as CSSProperties}>
            <b>Get the whole book</b>
            <p>
              After payment we draw the rest of the spreads and both covers; you can watch them
              appear.
            </p>
          </li>
          <li style={{ '--c': 'var(--ta-grass)' } as CSSProperties}>
            <b>Need more redos?</b>
            <p>
              When the included redos run out, add {pack.count} more for {pack.price}.
            </p>
          </li>
        </ul>
        <p className="ta-pricing-fine">
          {brand.priceNotes['en'] ?? ''} Read more in the{' '}
          <Link to={legal.routes.payment}>
            {legalLinkLabel(brand, legal, 'pricingRules', 'en')}
          </Link>
          .
        </p>

        <section className="ta-pricing-final" aria-labelledby="ta-start">
          <h2 id="ta-start" className="ta-h2">
            Start with the <span className="ta-o">free spread</span>
          </h2>
          <p>Pick the plan before the story is written; pay only after the first spread.</p>
          <Link to={createBook} className="ta-btn">
            Create a book
          </Link>
        </section>
      </main>
      <Footer page={page} />
    </div>
  );
}
